import React, { useCallback } from 'react';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';

import { Box, Button, Heading } from '@rover/kibble/core';
import WizardCollectionContainer from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/Collections/Wizard/WizardCollectionContainer';
import { getWizardTitle } from '@rover/react-lib/src/pages/search/SearchPage/utilities';
import { WizardPageType } from '@rover/react-lib/src/pages/search/SearchPage/WizardPageType';
import type { SearchFiltersOptions } from '@rover/types/src/apiResponses/SearchOptionsResponse';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import type { ServiceTypeOption } from '@rover/types/src/ServiceType';

export type Props = {
  fireSearchPageSearchCTAEvent: () => void;
  fireSearch: (arg0?: { searchInitiatedByMap: boolean }) => void;
  isCatOnly: boolean;
  onSubmit: () => void;
  searchFilters: SearchFilters;
  serviceTypeFilterOptions: SearchFiltersOptions;
  serviceTypeOptions: ServiceTypeOption[];
  updateFilters: (searchFilters: Partial<SearchFilters>) => void;
};

const WizardFiltersCollectionV2Base = ({
  fireSearchPageSearchCTAEvent,
  fireSearch,
  isCatOnly,
  onSubmit,
  searchFilters,
  serviceTypeFilterOptions,
  serviceTypeOptions,
  updateFilters,
}: Props): JSX.Element => {
  const { i18n } = useLingui();
  const handleChange = useCallback(
    (filters: Partial<SearchFilters>, shouldFireSearch = true): void => {
      updateFilters(filters);

      if (shouldFireSearch) {
        fireSearch();
      }
    },
    [updateFilters, fireSearch]
  );

  const handleCloseWizard = useCallback((): void => {
    fireSearchPageSearchCTAEvent();
    onSubmit();
  }, [fireSearchPageSearchCTAEvent, onSubmit]);

  return (
    <Box p="4x" pt="0x" borderBottom="1px solid" borderColor="border.primary">
      <Heading size="400" webProps={{ as: 'h4' }} pt="6x" mb="8x">
        {i18n._(getWizardTitle(isCatOnly))}
      </Heading>
      <WizardCollectionContainer
        isCatOnly={isCatOnly}
        onChange={handleChange}
        pageType={WizardPageType.INLINE}
        searchFilters={searchFilters}
        serviceTypeFilterOptions={serviceTypeFilterOptions}
        serviceTypeOptions={serviceTypeOptions}
      />
      <Button
        data-qa-id="wizard-search-collection-save-button"
        variant="primary"
        size="small"
        fullWidth
        onClick={handleCloseWizard}
        mt="8x"
        mb="12x"
      >
        <Trans>Find available sitters</Trans>
      </Button>
    </Box>
  );
};

const customComparison = (prevProps: Props, nextProps: Props): boolean =>
  JSON.stringify(prevProps.searchFilters) === JSON.stringify(nextProps.searchFilters) &&
  JSON.stringify(prevProps.serviceTypeFilterOptions) ===
    JSON.stringify(nextProps.serviceTypeFilterOptions) &&
  JSON.stringify(prevProps.serviceTypeOptions) === JSON.stringify(nextProps.serviceTypeOptions);

const WizardFiltersCollectionV2 = React.memo(WizardFiltersCollectionV2Base, customComparison);
export { WizardFiltersCollectionV2 };
export default WizardFiltersCollectionV2;
